/**
 * TutorialEngineAdapter
 *
 * CRITICAL ARCHITECTURAL SEPARATION:
 * This adapter handles ALL tutorial-specific logic that was polluting the core zodiacEngine.
 * The zodiacEngine should ONLY delegate to this adapter when in tutorial modes.
 *
 * Responsibilities:
 * - Tutorial configuration management
 * - Tutorial turn counting/limits
 * - Tutorial win/loss condition checking
 * - Tutorial AI behavior (Sentinel AI, overrides, FEN matching)
 * - Tutorial-specific game state modifications
 *
 * This keeps tutorial code completely separate from core game engine logic.
 */

import { GameState, IPiece, IMove, ISideEffect, Position, Mode, State, PieceType, MoveType } from './types';
import { ZodiacFEN } from './Fen/ZodiacFEN';
import { loadTestScenarioFEN } from './Fen/FenLoader';
import {
  findMatchingOverride,
  applyTutorialOverride,
  getTutorialOverrides,
  TutorialOverride
} from '../../components/Tutorials/TutorialOverride';
import { getTutorialSpec } from '../../components/Tutorials/TutorialSpecs';

/**
 * Tutorial configuration interface
 */
export interface TutorialConfig {
  constellation?: string;
  tutorialIndex?: number;
  realConstellation?: string;
  realTutorialIndex?: number;
  turnLimit?: number | 'unlimited';
  soloMode?: boolean;
  stalemateCallback?: (data: any) => void;
  uiLogger?: (message: string, type: 'info' | 'warning' | 'error' | 'success') => void;
}

/**
 * Result of tutorial checks
 */
export interface TutorialCheckResult {
  shouldBlockAction: boolean;
  reason?: string;
  tutorialEnded?: boolean;
  tutorialWon?: boolean;
  tutorialFailed?: boolean;
}

export class TutorialEngineAdapter {
  /**
   * Bear-5 diagnostic helper. Mirrors bear5DiagCensus in TutorialGame.tsx so
   * we can log the same census shape from the engine side. Detects whether
   * the board the engine is evaluating against has been mutated/cleared by
   * an AI search (see memory/alchemist-mcts-state-isolation.md).
   */
  public static bear5DiagCensus(board: any[][]): string {
    let lightBearKings = 0, lightBearsNonKing = 0;
    let darkRhinoKings = 0, darkRhinosNonKing = 0;
    let totalDark = 0;
    for (let r = 0; r < board.length; r++) {
      const row = board[r];
      if (!row) continue;
      for (let c = 0; c < row.length; c++) {
        const square = row[c];
        if (!square?.Pieces) continue;
        for (const p of square.Pieces) {
          if (!p) continue;
          if (!p.isLight) totalDark++;
          if (p.type === 'Bear' && p.isLight) {
            if (p.isKing) lightBearKings++; else lightBearsNonKing++;
          } else if (p.type === 'Rhino' && !p.isLight) {
            if (p.isKing) darkRhinoKings++; else darkRhinosNonKing++;
          }
        }
      }
    }
    return `lightBearKings=${lightBearKings} lightBearsNonKing=${lightBearsNonKing} darkRhinoKings=${darkRhinoKings} darkRhinosNonKing=${darkRhinosNonKing} totalDark=${totalDark}`;
  }

  /**
   * Bear-4 diagnostic helper. Mirrors bear4DiagCensus in TutorialGame.tsx.
   * Focuses on the dark crane king presence — Bear 4's secret fires when
   * `capture_target Crane dark` reads no dark crane on the snapshot.
   */
  public static bear4DiagCensus(board: any[][]): string {
    let lightBearKing = 0;
    let darkCraneKing = 0, darkCranesNonKing = 0;
    let totalPieces = 0;
    let craneAt = '';
    let bearAt = '';
    for (let r = 0; r < board.length; r++) {
      const row = board[r];
      if (!row) continue;
      for (let c = 0; c < row.length; c++) {
        const square = row[c];
        if (!square?.Pieces) continue;
        for (const p of square.Pieces) {
          if (!p) continue;
          totalPieces++;
          if (p.type === 'Bear' && p.isLight && p.isKing) { lightBearKing++; bearAt = `(${r},${c})`; }
          if (p.type === 'Crane' && !p.isLight) {
            if (p.isKing) { darkCraneKing++; craneAt = `(${r},${c})`; }
            else darkCranesNonKing++;
          }
        }
      }
    }
    return `lightBearKing=${lightBearKing}${bearAt ? '@' + bearAt : ''} darkCraneKing=${darkCraneKing}${craneAt ? '@' + craneAt : ''} darkCranesNonKing=${darkCranesNonKing} totalPieces=${totalPieces}`;
  }

  /**
   * Check if game state is in a tutorial mode
   */
  public static isInTutorialMode(gameState: GameState): boolean {
    const tutorialConfig = (gameState as any).tutorialConfig;
    return gameState.mode === Mode.Tutorial ||
           (gameState.mode === Mode.TestAI && tutorialConfig);
  }

  /**
   * Get tutorial config from game state
   */
  public static getTutorialConfig(gameState: GameState): TutorialConfig | null {
    return (gameState as any).tutorialConfig || null;
  }

  /**
   * Set tutorial configuration on game state
   */
  public static setTutorialConfig(gameState: GameState, config: TutorialConfig): void {
    (gameState as any).tutorialConfig = config;
  }

  /**
   * Check if turn limit has been reached in tutorial
   */
  public static checkTurnLimit(gameState: GameState): TutorialCheckResult {
    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) {
      return { shouldBlockAction: false };
    }

    const turnLimit = tutorialConfig.turnLimit;
    const currentTurnCount = gameState.turnCount;

    if (turnLimit && turnLimit !== 'unlimited' && currentTurnCount >= turnLimit) {
      return {
        shouldBlockAction: true,
        reason: 'Turn limit reached'
      };
    }

    return { shouldBlockAction: false };
  }

  /**
   * Check if a move will grant a bonus turn (to prevent premature turn counting)
   */
  public static willMoveGrantBonusTurn(moveType: MoveType, piece: IPiece): boolean {
    // Swing moves always grant bonus turns for monkeys
    if (moveType === MoveType.swing && piece.type === PieceType.Monkey) {
      return true;
    }

    // Consume moves always grant bonus turns for monkeys
    if (moveType === MoveType.consume && piece.type === PieceType.Monkey) {
      return true;
    }

    // Infect moves always grant bonus turns for viruses
    if (moveType === MoveType.infect && piece.type === PieceType.Virus) {
      return true;
    }

    return false;
  }

  /**
   * Handle tutorial turn increment logic before a move is made
   */
  public static handleTutorialTurnIncrement(
    gameState: GameState,
    piece: IPiece,
    moveType: MoveType,
    incrementTurnFn: () => void
  ): void {
    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) return;

    const isSoloMode = tutorialConfig.soloMode || false;


    if (piece.isLight) {
      // In any tutorial mode, increment turn count BEFORE light player makes a move
      // BONUS TURN FIX: Don't increment if this move will grant a bonus turn
      const willGrantBonusTurn = this.willMoveGrantBonusTurn(moveType, piece);

      if (willGrantBonusTurn) {
      } else {
        incrementTurnFn();
      }
    }
  }

  /**
   * Check tutorial failure conditions after a move
   */
  public static async checkTutorialFailure(
    gameState: GameState,
    piece: IPiece,
    uiLogger?: (message: string, type: 'info' | 'warning' | 'error') => void
  ): Promise<TutorialCheckResult> {
    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) {
      return { shouldBlockAction: false };
    }

    const { checkTutorialFailure } = require('../../components/Tutorials/TutorialWinConditions');

    // Use realConstellation if available, otherwise fall back to constellation
    const constellation = tutorialConfig.realConstellation || tutorialConfig.constellation;
    const tutorialIndex = tutorialConfig.realTutorialIndex !== undefined
      ? tutorialConfig.realTutorialIndex
      : tutorialConfig.tutorialIndex;

    if (!constellation || tutorialIndex === undefined) {
      return { shouldBlockAction: false };
    }

    const hasFailed = checkTutorialFailure(
      constellation,
      tutorialIndex,
      gameState.turnCount,
      gameState.board
    );

    if (hasFailed && uiLogger) {
      uiLogger(
        `💀 IMMEDIATE LOSS DETECTED: Tutorial failed after ${
          piece.isLight ? 'Light' : 'Dark'
        } move`,
        'error'
      );

      // Set tutorial failure state
      gameState.state = State.PostGame;
      gameState.lightWon = false; // Tutorial failure means Light loses
      (gameState as any).tutorialFailed = true;

      return {
        shouldBlockAction: true,
        tutorialEnded: true,
        tutorialFailed: true
      };
    }

    return { shouldBlockAction: false };
  }

  /**
   * Check tutorial win conditions in checkGameEnd
   */
  public static checkTutorialWinConditions(
    gameState: GameState,
    notifyListenersFn: () => void,
    sendUpdateToRemoteFn: () => void
  ): TutorialCheckResult {
    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) {
      return { shouldBlockAction: false };
    }

    const uiLogger = tutorialConfig.uiLogger;

    // Get all pieces on the board to find the last moved piece
    let lastMovedPiece = null;
    let lastMovedPosition = null;

    // Find any light piece (assuming player controls light pieces in tutorials)
    outerLoop: for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight) {
            lastMovedPiece = piece;
            lastMovedPosition = { row, col };
            break outerLoop;
          }
        }
      }
    }

    // EMERGENCY OVERRIDE: Fix tutorial configuration mismatches
    const correctedConfig = this.applyEmergencyOverrides(gameState, tutorialConfig);
    let emergencyOverrideApplied = correctedConfig !== tutorialConfig;

    // Run emergency override win check if applied
    if (emergencyOverrideApplied && lastMovedPiece && lastMovedPosition) {
      const overrideResult = this.checkTutorialWinWithConfig(
        correctedConfig,
        lastMovedPosition,
        lastMovedPiece,
        gameState,
        uiLogger,
        notifyListenersFn,
        sendUpdateToRemoteFn
      );

      if (overrideResult.tutorialEnded) {
        return overrideResult;
      }
    }

    // Run normal tutorial win check if not overridden
    if (lastMovedPiece && lastMovedPosition && !emergencyOverrideApplied) {
      return this.checkTutorialWinWithConfig(
        tutorialConfig,
        lastMovedPosition,
        lastMovedPiece,
        gameState,
        uiLogger,
        notifyListenersFn,
        sendUpdateToRemoteFn
      );
    }

    return { shouldBlockAction: false };
  }

  /**
   * Apply emergency overrides for tutorial configuration mismatches
   */
  private static applyEmergencyOverrides(
    gameState: GameState,
    tutorialConfig: TutorialConfig
  ): TutorialConfig {
    // Check for Tree tutorial 2 that's actually a different tutorial
    if (tutorialConfig.constellation === 'Tree' && tutorialConfig.tutorialIndex === 1) {

      const realConstellation = tutorialConfig.realConstellation;

      if (realConstellation === 'Bear') {
        // Race defence is the only Bear tutorial that needs king-protection
        // remapping. Its 0-based index follows the data swap in TutorialFENs /
        // TutorialWinConditions — use realTutorialIndex so this stays correct
        // if the tutorials are reordered again. Falls back to 3 (Race defence
        // at the time of writing) when no real index was preserved.
        return {
          ...tutorialConfig,
          constellation: 'Bear',
          tutorialIndex: tutorialConfig.realTutorialIndex ?? 3,
          realConstellation: tutorialConfig.constellation,
          realTutorialIndex: tutorialConfig.tutorialIndex,
        };
      } else if (realConstellation === 'Crane') {
      } else if (realConstellation === 'Rhino') {
        return {
          ...tutorialConfig,
          constellation: 'Rhino',
          tutorialIndex: 0, // Rhino Tutorial 1 (0-based index)
          realConstellation: tutorialConfig.constellation,
          realTutorialIndex: tutorialConfig.tutorialIndex,
        };
      }
    }

    return tutorialConfig;
  }

  /**
   * Check tutorial win condition with a specific config
   */
  private static checkTutorialWinWithConfig(
    config: TutorialConfig,
    lastMovedPosition: Position,
    lastMovedPiece: IPiece,
    gameState: GameState,
    uiLogger: any,
    notifyListenersFn: () => void,
    sendUpdateToRemoteFn: () => void
  ): TutorialCheckResult {
    try {
      const { checkTutorialWinCondition, isSecretVictoryEligible } = require('../../components/Tutorials/TutorialWinConditions');

      const isBear5 = config.constellation === 'Bear' && config.tutorialIndex === 4;

      if (isBear5) {
        // eslint-disable-next-line no-console
        console.warn(`[BEAR5-DIAG] ENTRY src=engine turn=${gameState.turnCount} propsConst=${config.constellation} propsIdx=${config.tutorialIndex}`);
        // eslint-disable-next-line no-console
        console.warn(`[BEAR5-DIAG] CENSUS src=engine turn=${gameState.turnCount} ${TutorialEngineAdapter.bear5DiagCensus(gameState.board)}`);
      }

      const isBear4 = config.constellation === 'Bear' && config.tutorialIndex === 3;
      if (isBear4) {
        // eslint-disable-next-line no-console
        console.warn(`[BEAR4-DIAG] ENTRY src=engine turn=${gameState.turnCount} propsConst=${config.constellation} propsIdx=${config.tutorialIndex}`);
        // eslint-disable-next-line no-console
        console.warn(`[BEAR4-DIAG] CENSUS src=engine turn=${gameState.turnCount} ${TutorialEngineAdapter.bear4DiagCensus(gameState.board)}`);
      }

      const winType = checkTutorialWinCondition(
        config.constellation!,
        config.tutorialIndex!,
        lastMovedPosition,
        lastMovedPiece,
        gameState.turnCount,
        gameState.board
      );

      // Universal turn-1 secret-victory backstop on the engine side.
      // Without this, a spurious secret-victory return here flips
      // gameState.state = PostGame and gameState.lightWon = true on the live
      // state, which the UI then renders as a victory regardless of the
      // React-side guards. Single source of truth lives in
      // isSecretVictoryEligible (TutorialWinConditions).
      if (!isSecretVictoryEligible(
        winType,
        gameState.turnCount,
        'engine',
        `tut=${config.constellation}/${config.tutorialIndex}`
      )) {
        return { shouldBlockAction: false };
      }

      if (winType === 'regular' || winType === 'secret') {

        // Live-state cross-check for `secret` wins. Walks the secretWin tree
        // (including `multiple`/`all_of` wrappers) and re-evaluates EVERY
        // leaf predicate against the live engine board. A `multiple` is OR,
        // an `all_of` is AND — so we re-compute the same boolean over the
        // live board and refuse to fire if it doesn't agree with the
        // snapshot-based `winType === 'secret'`. Catches: stale snapshots
        // (Bear-4/5 false fires) AND in-place mutations during AI search
        // (alchemist-mcts-state-isolation pattern). Handles both
        // `capture_target` and `reach_row` — earlier guards only checked
        // top-level `capture_target` and missed Bear[3]'s `multiple` wrap.
        if (winType === 'secret') {
          try {
            const { TUTORIAL_WIN_CONFIGS } = require('../../components/Tutorials/TutorialWinConditions');
            const cfg = TUTORIAL_WIN_CONFIGS?.[config.constellation!]?.[config.tutorialIndex!];
            const sec = cfg?.secretWin;

            const evalLeaf = (node: any, board: any[][]): boolean => {
              if (!node) return false;
              if (node.type === 'capture_target' && node.target && node.targetSide) {
                // Target captured iff no surviving piece matches.
                for (const r of board) {
                  if (!r) continue;
                  for (const sq of r) {
                    if (!sq?.Pieces) continue;
                    for (const p of sq.Pieces) {
                      if (!p) continue;
                      if (p.type === node.target &&
                          p.isLight === (node.targetSide === 'light') &&
                          (!node.requireKing || p.isKing === true)) {
                        return false; // target still alive — leaf fails
                      }
                    }
                  }
                }
                return true;
              }
              if (node.type === 'reach_row' && node.target && node.targetSide && typeof node.row === 'number') {
                const row = board[node.row];
                if (!row) return false;
                for (const sq of row) {
                  if (!sq?.Pieces) continue;
                  for (const p of sq.Pieces) {
                    if (!p) continue;
                    if (p.type === node.target &&
                        p.isLight === (node.targetSide === 'light') &&
                        (!node.requireKing || p.isKing === true)) {
                      return true;
                    }
                  }
                }
                return false;
              }
              // Non-leaf or unsupported predicate — be permissive so we
              // don't block legitimate wins driven by predicates we
              // haven't taught the verifier about.
              return true;
            };

            const evalTree = (node: any, board: any[][]): boolean => {
              if (!node) return false;
              if (node.type === 'multiple' && Array.isArray(node.conditions)) {
                return node.conditions.some((c: any) => evalTree(c, board));
              }
              if (node.type === 'all_of' && Array.isArray(node.conditions)) {
                return node.conditions.every((c: any) => evalTree(c, board));
              }
              return evalLeaf(node, board);
            };

            const liveSaysSecret = evalTree(sec, gameState.board);
            if (!liveSaysSecret) {
              // eslint-disable-next-line no-console
              console.warn(`[TUT-DIAG] SECRET-LIVE-STATE-DISAGREES src=engine tut=${config.constellation}/${config.tutorialIndex} turn=${gameState.turnCount} secretWinType=${sec?.type} — checkTutorialWinCondition said secret but live-board tree-eval disagrees`);
              return { shouldBlockAction: false };
            }
          } catch (_xcheckErr) {
            // Best-effort guard — never let it block a legitimate win.
          }
        }

        if (isBear5 && winType === 'secret') {
          const stack = (new Error().stack || '').split('\n').slice(1, 7).join(' | ');
          // eslint-disable-next-line no-console
          console.warn(`[BEAR5-DIAG] SECRET-FIRED src=engine turn=${gameState.turnCount} trigger=${lastMovedPiece?.type} stack=${stack}`);
        }
        if (isBear4 && winType === 'secret') {
          const stack = (new Error().stack || '').split('\n').slice(1, 7).join(' | ');
          // eslint-disable-next-line no-console
          console.warn(`[BEAR4-DIAG] SECRET-FIRED src=engine turn=${gameState.turnCount} trigger=${lastMovedPiece?.type} stack=${stack}`);
        }

        // Set game state to PostGame to trigger UI victory handling
        gameState.state = State.PostGame;
        gameState.lightWon = true; // Player (light) wins in tutorials

        // Notify UI immediately
        notifyListenersFn();
        sendUpdateToRemoteFn();

        if (uiLogger) {
          uiLogger(`🏆 Tutorial Complete! Victory type: ${winType}`, 'success');
        }

        return {
          shouldBlockAction: true,
          tutorialEnded: true,
          tutorialWon: true
        };
      }
    } catch (error) {
    }

    return { shouldBlockAction: false };
  }

  /**
   * Handle tutorial-specific turn changes
   */
  public static handleTutorialTurnChange(
    gameState: GameState,
    finalizeFENFn: () => void,
    notifyListenersFn: () => void,
    sendUpdateToRemoteFn: () => void
  ): TutorialCheckResult {
    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) {
      return { shouldBlockAction: false };
    }

    const isSoloMode = tutorialConfig.soloMode || false;
    const uiLogger = tutorialConfig.uiLogger;


    // In solo mode, skip dark turn changes
    if (isSoloMode && gameState.isLightTurn) {
      finalizeFENFn();
      notifyListenersFn();
      sendUpdateToRemoteFn();
      return { shouldBlockAction: true, reason: 'Solo mode - no turn change' };
    }

    if (uiLogger) {
      uiLogger(
        `🎓 Tutorial Turn Debug: Before changeTurns - isLightTurn=${gameState.isLightTurn}, turnCount=${gameState.turnCount}`,
        'info'
      );
    }

    return { shouldBlockAction: false };
  }

  /**
   * Check if AI should be blocked in tutorial mode
   */
  public static shouldBlockAIMove(gameState: GameState): TutorialCheckResult {
    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) {
      return { shouldBlockAction: false };
    }

    // Check for tutorial failure
    const tutorialFailed = (gameState as any).tutorialFailed;
    if (tutorialFailed) {
      return {
        shouldBlockAction: true,
        reason: 'Tutorial already failed',
        tutorialFailed: true
      };
    }

    // Check for solo mode
    const isSoloMode = tutorialConfig.soloMode || false;
    if (gameState.mode === Mode.Tutorial && isSoloMode) {
      return {
        shouldBlockAction: true,
        reason: 'Solo tutorial mode'
      };
    }

    return { shouldBlockAction: false };
  }

  /**
   * Check for tutorial FEN overrides and apply if found
   */
  public static async checkAndApplyTutorialOverride(
    gameState: GameState,
    engine: any
  ): Promise<TutorialCheckResult> {
    if (gameState.mode !== Mode.TestAI) {
      return { shouldBlockAction: false };
    }

    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) {
      return { shouldBlockAction: false };
    }

    // Get current board state as FEN
    const currentFEN = ZodiacFEN.encode(gameState);
    const uiLogger = tutorialConfig.uiLogger;

    // Use realConstellation and realTutorialIndex for FEN override lookups
    let constellation = tutorialConfig.realConstellation || tutorialConfig.constellation;
    let tutorialIndex = tutorialConfig.realTutorialIndex !== undefined
      ? tutorialConfig.realTutorialIndex
      : tutorialConfig.tutorialIndex;


    if (!constellation || tutorialIndex === undefined) {
      return { shouldBlockAction: false };
    }

    // Log override check
    if (uiLogger) {
      uiLogger(`🎯 AI Override Check: ${constellation} tutorial ${tutorialIndex + 1}`, 'info');

      const overrides = getTutorialOverrides(constellation, tutorialIndex);
      uiLogger(`🎯 CURRENT: ${currentFEN}`, 'info');
      overrides.forEach((override, index) => {
        uiLogger(`🎯 TRIGGER${index + 1}: ${override.triggerFEN}`, 'info');
      });
    }

    // Create debug logger
    const debugLogger = (override: TutorialOverride, constellation: string, tutorialIndex: number) => {
      if (uiLogger) {
        uiLogger(`🎯 AI Force Move Triggered: ${constellation} tutorial ${tutorialIndex + 1}`, 'info');
        uiLogger(`🎯 Matched State: ${override.triggerFEN}`, 'info');
        uiLogger(`🎯 Forcing Response: ${override.responseFEN}`, 'info');
        if (override.description) {
          uiLogger(`🎯 ${override.description}`, 'info');
        }
      }
    };

    (debugLogger as any).uiLogger = uiLogger;

    const matchingOverride = findMatchingOverride(constellation, tutorialIndex, currentFEN, debugLogger);

    if (!matchingOverride && uiLogger) {
      uiLogger(`🎯 No override match found - AI will use normal logic`, 'info');
    }

    if (matchingOverride) {
      // Apply the override with delay
      await applyTutorialOverride(
        matchingOverride,
        (responseFEN) => {
          loadTestScenarioFEN(engine, responseFEN, true); // Preserve turn count
          engine.eventManager.notifyLocalListeners();
        },
        (override) => {
          if (uiLogger) {
            uiLogger(`🎯 AI Force Move Applied: Board state updated`, 'info');
          }
        },
        () => engine.getGameState()
      );

      return {
        shouldBlockAction: true,
        reason: 'Tutorial override applied'
      };
    }

    return { shouldBlockAction: false };
  }

  /**
   * Get the appropriate AI for tutorial mode
   */
  public static async makeTutorialAIMove(
    gameState: GameState,
    engine: any
  ): Promise<boolean> {
    const tutorialConfig = this.getTutorialConfig(gameState);
    if (!tutorialConfig) {
      return false;
    }

    const isSoloMode = tutorialConfig.soloMode || false;
    const uiLogger = tutorialConfig.uiLogger;

    // Use realConstellation if available
    const constellation = tutorialConfig.realConstellation || tutorialConfig.constellation;
    const tutorialIndex = tutorialConfig.realTutorialIndex !== undefined
      ? tutorialConfig.realTutorialIndex
      : tutorialConfig.tutorialIndex;

    if (!constellation || tutorialIndex === undefined) {
      return false;
    }


    if (isSoloMode) {
      return false;
    }

    // Check for FEN overrides FIRST before any AI logic
    const currentFEN = ZodiacFEN.encode(gameState);
    const overrides = getTutorialOverrides(constellation, tutorialIndex);

    if (overrides.length > 0) {
      if (uiLogger) {
        uiLogger(`🎯 Checking ${overrides.length} override(s) for ${constellation} tutorial ${tutorialIndex + 1}`, 'info');
        uiLogger(`🎯 Current FEN: ${currentFEN}`, 'info');
      }

      const matchingOverride = findMatchingOverride(constellation, tutorialIndex, currentFEN);

      if (matchingOverride) {
        if (uiLogger) {
          uiLogger(`🎯 Override MATCHED! Applying response FEN`, 'info');
        }

        await applyTutorialOverride(
          matchingOverride,
          (responseFEN) => {
            loadTestScenarioFEN(engine, responseFEN, true);
            engine.eventManager.notifyLocalListeners();
          },
          (override) => {
            if (uiLogger) {
              uiLogger(`🎯 Override applied: ${override.responseFEN}`, 'info');
            }
          },
          () => engine.getGameState()
        );

        // Surface a "scripted move" plan so the tutorial UI doesn't silently
        // appear unresponsive when an override fires. No PV available — the
        // override is deterministic and bypasses Sentinel.
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('tutorialAIPlan', {
              detail: {
                constellation,
                tutorialIndex,
                scripted: true,
                triggerFEN: matchingOverride.triggerFEN,
                responseFEN: matchingOverride.responseFEN,
                currentFEN,
                overrideTriggers: overrides.map((o) => o.triggerFEN),
                ai: null,
                expectedHumanResponse: null,
                evaluation: 0,
                reasoning: 'Scripted move (override match)',
                pvLength: 0,
              },
            }));
          }
        } catch (_err) {
          // Best-effort UI signal — never let it block the override.
        }

        return true;
      } else {
        if (uiLogger) {
          uiLogger(`🎯 No override match - falling back to AI`, 'info');
        }
      }
    }

    // SPECIAL CASE: Use MCTS AI for Bear tutorial 2 (index 1)
    if (constellation === 'Bear' && tutorialIndex === 1) {
      if (uiLogger) {
        uiLogger(`🧠 Using MCTS Priority AI for Bear Tutorial ${tutorialIndex + 1}`, 'info');
      }

      // Use the standard AI controller
      if (!engine.aiController) {
        const { AIController } = require('./AIController');
        engine.aiController = new AIController(engine);
        engine.aiController.setSearchIterations(500);
      }

      try {
        const moveResult = await engine.aiController.makeMove();
        return moveResult !== null;
      } catch (error) {
        if (uiLogger) {
          uiLogger(`❌ MCTS AI error: ${error}`, 'error');
        }
        return false;
      }
    }

    // Bear tutorial 3 (index 2) "Race Defence" - uses default Sentinel AI below

    // Use Sentinel AI for all other non-solo tutorials
    if (uiLogger) {
      uiLogger(`🧠 Using Sentinel AI for ${constellation} Tutorial ${tutorialIndex + 1}`, 'info');
    }

    try {
      const { MinimaxAI, MinimaxModel } = require('./AI/MinimaxAI');
      const minimaxAI = new MinimaxAI(engine, MinimaxModel.SENTINEL);

      // Per-tutorial Sentinel depth cap. Tutorials like Predator set this to
      // 2 so the AI sees immediate threats but doesn't search deep enough to
      // realise the position is lost, which makes all moves tie at -100000
      // and the AI play randomly.
      const tutorialSpec = getTutorialSpec(constellation, tutorialIndex);
      if (tutorialSpec.maxAIDepth) {
        minimaxAI.setMaxDepthCap(tutorialSpec.maxAIDepth);
        if (uiLogger) {
          uiLogger(`🧠 Sentinel depth capped at ${tutorialSpec.maxAIDepth} ply for this tutorial`, 'info');
        }
      }

      const maxThinkingTime = 5000;

      const aiMoveResult = await minimaxAI.selectMove(gameState, maxThinkingTime);

      if (aiMoveResult && aiMoveResult.move) {
        const fromPosition = aiMoveResult.fromPosition;
        const move = aiMoveResult.move;

        const square = gameState.board[fromPosition.row][fromPosition.col];
        const piece = square.Pieces.find((p) => p.id === move.pieceId);

        // Publish the AI's plan so the tutorial UI can show what move it
        // picked, what it expects the player to do next, and the score.
        // Piece-type lookup uses the PRE-MOVE board (gameState) since piece
        // IDs in the PV are anchored to that state.
        try {
          if (typeof window !== 'undefined') {
            const lookupPieceType = (board: any[][], pieceId: string): string | undefined => {
              for (const row of board) {
                for (const sq of row) {
                  if (!sq?.Pieces) continue;
                  const found = sq.Pieces.find((p: any) => p.id === pieceId);
                  if (found) return found.type;
                }
              }
              return undefined;
            };
            const aiPieceType =
              (aiMoveResult.movingPiece && aiMoveResult.movingPiece.type) ||
              (piece && piece.type) ||
              lookupPieceType(gameState.board, move.pieceId);
            const pv: any[] = aiMoveResult.principalVariation || [];
            const expected = pv.length > 1 ? pv[1] : null;
            const expectedPieceType = expected
              ? lookupPieceType(gameState.board, expected.move.pieceId)
              : undefined;

            // Sentinel returns score from the AI's perspective (positive =
            // good for whoever is to move). Convert to absolute Light − Dark
            // so the panel can label "favors Light/Dark" consistently:
            // if the AI is Dark, negate the score.
            const isAILight = gameState.isLightTurn;
            const rawEval = aiMoveResult.evaluation ?? 0;
            const absoluteEval = isAILight ? rawEval : -rawEval;

            const detail = {
              constellation,
              tutorialIndex,
              model: 'Sentinel',
              ai: {
                from: aiMoveResult.fromPosition,
                to: aiMoveResult.move.position,
                pieceType: aiPieceType,
                isCapture: aiMoveResult.move.toPiece !== null && aiMoveResult.move.toPiece !== undefined,
              },
              expectedHumanResponse: expected
                ? {
                    from: expected.fromPosition,
                    to: expected.move.position,
                    pieceType: expectedPieceType,
                    isCapture: expected.move.toPiece !== null && expected.move.toPiece !== undefined,
                  }
                : null,
              evaluation: absoluteEval,
              rawEvaluation: rawEval,
              aiSide: isAILight ? 'Light' : 'Dark',
              reasoning: aiMoveResult.reasoning,
              pvLength: pv.length,
              currentFEN,
              overrideTriggers: overrides.map((o) => o.triggerFEN),
            };
            window.dispatchEvent(new CustomEvent('tutorialAIPlan', { detail }));
          }
        } catch (_err) {
          // Best-effort UI signal — never let it block the AI move.
        }

        if (piece) {
          const moveResult = await engine.makeMove(
            fromPosition,
            move.position,
            piece,
            move.toPiece,
            move.moveType,
            move.sideEffects
          );

          if (moveResult) {
            if (uiLogger) {
              uiLogger(
                `🧠 Sentinel AI move applied successfully (score: ${aiMoveResult.evaluation.toFixed(0)})`,
                'info'
              );
            }
            return true;
          } else {
            if (uiLogger) {
              uiLogger(`❌ Sentinel AI move failed to apply`, 'error');
            }
          }
        } else {
          if (uiLogger) {
            uiLogger(`❌ Sentinel AI could not find piece ${move.pieceId}`, 'error');
          }
        }
      } else {
        if (uiLogger) {
          uiLogger(`❌ Sentinel AI could not find a move`, 'error');
        }
      }
    } catch (error) {
      if (uiLogger) {
        uiLogger(`❌ Sentinel AI error: ${error}`, 'error');
      }
      return false;
    }

    return false;
  }
}
